import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { X, Search, Car, Truck, CircleDot } from 'lucide-react';

// Common tire sizes for quick selection
const COMMON_TIRE_SIZES = {
  car: [
    "195/65R15", "205/55R16", "205/60R16", "215/55R16", "215/60R16",
    "225/45R17", "225/50R17", "235/45R17", "225/40R18", "235/40R18"
  ],
  truck: [
    "295/80R22.5", "315/70R22.5", "315/80R22.5", "385/55R22.5", "385/65R22.5"
  ],
  tractor: [
    "280/85R24", "320/85R24", "420/85R28", "420/85R30", "480/80R26", "520/85R38"
  ]
};

const VEHICLE_TYPES = [
  { value: "car", label: "Araba / Binek", icon: Car },
  { value: "truck", label: "Kamyon", icon: Truck },
  { value: "tir", label: "TIR / Dorse", icon: Truck },
  { value: "tractor", label: "Traktör", icon: CircleDot }
];

const TIRE_SEASONS = [
  { value: "summer", label: "Yaz Lastiği" },
  { value: "winter", label: "Kış Lastiği" },
  { value: "all-season", label: "4 Mevsim" }
];

const RIM_SIZES = ["15", "16", "17", "18", "19", "20", "22.5"];

export const ProductFilters = ({ categories = [], onFilterChange }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tireSizeInput, setTireSizeInput] = useState('');
  
  const currentCategory = searchParams.get('category');
  const currentMinPrice = searchParams.get('min_price') || 0;
  const currentMaxPrice = searchParams.get('max_price') || 1000;
  const currentFeatured = searchParams.get('featured') === 'true';
  
  // Tire-specific filters
  const currentTireSize = searchParams.get('tire_size') || '';
  const currentVehicleType = searchParams.get('vehicle_type') || '';
  const currentTireSeason = searchParams.get('tire_season') || '';
  const currentRimSize = searchParams.get('rim_size') || '';
  
  const isTiresCategory = currentCategory === 'tires';
  const isAutoPartsCategory = currentCategory === 'auto-parts';

  const handleCategoryChange = (slug) => {
    const params = new URLSearchParams(searchParams);
    if (currentCategory === slug) {
      params.delete('category');
      // Clear tire-specific filters when leaving tires category
      params.delete('tire_size');
      params.delete('vehicle_type');
      params.delete('tire_season');
      params.delete('rim_size');
    } else {
      params.set('category', slug);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePriceChange = (values) => {
    const params = new URLSearchParams(searchParams);
    params.set('min_price', values[0]);
    params.set('max_price', values[1]);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleFeaturedChange = (checked) => {
    const params = new URLSearchParams(searchParams);
    if (checked) {
      params.set('featured', 'true');
    } else {
      params.delete('featured');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  // Tire-specific filter handlers
  const handleTireSizeSearch = () => {
    if (!tireSizeInput.trim()) return;
    const params = new URLSearchParams(searchParams);
    params.set('tire_size', tireSizeInput.trim());
    params.set('page', '1');
    // Ensure we're in tires category
    if (!currentCategory) {
      params.set('category', 'tires');
    }
    setSearchParams(params);
  };

  const handleTireSizeSelect = (size) => {
    const params = new URLSearchParams(searchParams);
    if (currentTireSize === size) {
      params.delete('tire_size');
    } else {
      params.set('tire_size', size);
    }
    params.set('page', '1');
    setSearchParams(params);
    setTireSizeInput(size);
  };

  const handleVehicleTypeChange = (type) => {
    const params = new URLSearchParams(searchParams);
    if (currentVehicleType === type) {
      params.delete('vehicle_type');
    } else {
      params.set('vehicle_type', type);
    }
    params.set('page', '1');
    // Ensure we're in tires category
    if (!currentCategory) {
      params.set('category', 'tires');
    }
    setSearchParams(params);
  };

  const handleTireSeasonChange = (season) => {
    const params = new URLSearchParams(searchParams);
    if (currentTireSeason === season) {
      params.delete('tire_season');
    } else {
      params.set('tire_season', season);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleRimSizeChange = (size) => {
    const params = new URLSearchParams(searchParams);
    if (currentRimSize === size) {
      params.delete('rim_size');
    } else {
      params.set('rim_size', size);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
    setTireSizeInput('');
  };

  const clearTireFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('tire_size');
    params.delete('vehicle_type');
    params.delete('tire_season');
    params.delete('rim_size');
    params.set('page', '1');
    setSearchParams(params);
    setTireSizeInput('');
  };

  const hasFilters = currentCategory || currentMinPrice > 0 || currentMaxPrice < 1000 || currentFeatured;
  const hasTireFilters = currentTireSize || currentVehicleType || currentTireSeason || currentRimSize;

  useEffect(() => {
    if (currentTireSize) setTireSizeInput(currentTireSize);
  }, [currentTireSize]);

  return (
    <div className="space-y-6" data-testid="product-filters">
      {/* Clear filters */}
      {hasFilters && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={clearFilters}
          className="w-full justify-start text-muted-foreground"
          data-testid="clear-filters-btn"
        >
          <X className="w-4 h-4 mr-2" />
          {t('filters.clearAll')}
        </Button>
      )}

      <Accordion type="multiple" defaultValue={['categories', 'price', 'other', 'tire-size', 'vehicle-type', 'tire-season']} className="w-full">
        {/* Categories */}
        <AccordionItem value="categories">
          <AccordionTrigger className="text-sm font-medium">{t('filters.categories')}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-2">
                  <Checkbox
                    id={cat.slug}
                    checked={currentCategory === cat.slug}
                    onCheckedChange={() => handleCategoryChange(cat.slug)}
                    data-testid={`filter-category-${cat.slug}`}
                  />
                  <Label 
                    htmlFor={cat.slug} 
                    className="text-sm font-normal cursor-pointer"
                  >
                    {t(`categories.${cat.slug}`, cat.name)}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Tire Size Filter - Show for tires category or always visible */}
        {(isTiresCategory || !currentCategory) && (
          <AccordionItem value="tire-size">
            <AccordionTrigger className="text-sm font-medium">
              <div className="flex items-center gap-2">
                <CircleDot className="w-4 h-4 text-primary" />
                Lastik Ebatı
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                {/* Tire size search input */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Örn: 205/55R16"
                    value={tireSizeInput}
                    onChange={(e) => setTireSizeInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleTireSizeSearch()}
                    className="flex-1"
                    data-testid="tire-size-input"
                  />
                  <Button 
                    size="icon" 
                    variant="outline"
                    onClick={handleTireSizeSearch}
                    data-testid="tire-size-search-btn"
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Current tire size badge */}
                {currentTireSize && (
                  <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-lg">
                    <span className="text-sm font-medium text-primary">{currentTireSize}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleTireSizeSelect('')}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}

                {/* Common tire sizes */}
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Popüler Ebatlar:</p>
                  <div className="flex flex-wrap gap-1">
                    {COMMON_TIRE_SIZES.car.slice(0, 6).map((size) => (
                      <Button
                        key={size}
                        variant={currentTireSize === size ? "default" : "outline"}
                        size="sm"
                        className="text-xs h-7 px-2"
                        onClick={() => handleTireSizeSelect(size)}
                        data-testid={`tire-size-${size}`}
                      >
                        {size}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Vehicle Type Filter */}
        {(isTiresCategory || !currentCategory) && (
          <AccordionItem value="vehicle-type">
            <AccordionTrigger className="text-sm font-medium">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-primary" />
                Araç Tipi
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3">
                {VEHICLE_TYPES.map(({ value, label, icon: Icon }) => (
                  <div key={value} className="flex items-center gap-2">
                    <Checkbox
                      id={`vehicle-${value}`}
                      checked={currentVehicleType === value}
                      onCheckedChange={() => handleVehicleTypeChange(value)}
                      data-testid={`filter-vehicle-${value}`}
                    />
                    <Label 
                      htmlFor={`vehicle-${value}`} 
                      className="text-sm font-normal cursor-pointer flex items-center gap-2"
                    >
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Tire Season Filter */}
        {(isTiresCategory || !currentCategory) && (
          <AccordionItem value="tire-season">
            <AccordionTrigger className="text-sm font-medium">Mevsim</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3">
                {TIRE_SEASONS.map(({ value, label }) => (
                  <div key={value} className="flex items-center gap-2">
                    <Checkbox
                      id={`season-${value}`}
                      checked={currentTireSeason === value}
                      onCheckedChange={() => handleTireSeasonChange(value)}
                      data-testid={`filter-season-${value}`}
                    />
                    <Label 
                      htmlFor={`season-${value}`} 
                      className="text-sm font-normal cursor-pointer"
                    >
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Rim Size Filter */}
        {(isTiresCategory || !currentCategory) && (
          <AccordionItem value="rim-size">
            <AccordionTrigger className="text-sm font-medium">Jant Çapı</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-wrap gap-2">
                {RIM_SIZES.map((size) => (
                  <Button
                    key={size}
                    variant={currentRimSize === size ? "default" : "outline"}
                    size="sm"
                    className="h-8 px-3"
                    onClick={() => handleRimSizeChange(size)}
                    data-testid={`rim-size-${size}`}
                  >
                    {size}&quot;
                  </Button>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Clear tire filters button */}
        {hasTireFilters && (
          <div className="pt-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearTireFilters}
              className="w-full text-muted-foreground"
            >
              <X className="w-4 h-4 mr-2" />
              Lastik Filtrelerini Temizle
            </Button>
          </div>
        )}

        {/* Price Range */}
        <AccordionItem value="price">
          <AccordionTrigger className="text-sm font-medium">{t('filters.priceRange')}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <Slider
                defaultValue={[Number(currentMinPrice), Number(currentMaxPrice)]}
                max={1000}
                step={10}
                onValueCommit={handlePriceChange}
                className="w-full"
                data-testid="price-slider"
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>${currentMinPrice}</span>
                <span>${currentMaxPrice}</span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Other filters */}
        <AccordionItem value="other">
          <AccordionTrigger className="text-sm font-medium">{t('filters.other')}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="featured"
                  checked={currentFeatured}
                  onCheckedChange={handleFeaturedChange}
                  data-testid="filter-featured"
                />
                <Label htmlFor="featured" className="text-sm font-normal cursor-pointer">
                  {t('filters.featuredOnly')}
                </Label>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
